"use client";

import authAxiosInstance from "@/lib/authAxios";
import { useTranslation } from "@/lib/i18n/react-i18next";
import {
  isTimeSyncAuthError,
  isTimeSyncError,
  TIME_SYNC_ERROR_EVENT,
  TIME_SYNC_RECHECK_HEADER,
  type TimeSyncSnapshot,
} from "@/lib/timeSync";
import { cn } from "@/utils/cn";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { ClockAlert, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

function formatTime(value?: number | null) {
  if (!value) return "-";
  return dayjs(value).format("DD MMM'YY HH:mm Z");
}

function normalizeServerTime(value: unknown) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed < 10_000_000_000 ? parsed * 1000 : parsed;
}

export function TimeSyncGuard() {
  const { t } = useTranslation(["toast", "common"]);
  const queryClient = useQueryClient();
  const [snapshot, setSnapshot] = useState<TimeSyncSnapshot | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);

  useEffect(() => {
    const handleTimeSyncError = (event: Event) => {
      const detail = event instanceof CustomEvent ? event.detail : undefined;
      setSnapshot({
        deviceTime: typeof detail?.deviceTime === "number" ? detail.deviceTime : Date.now(),
        serverTime: typeof detail?.serverTime === "number" ? detail.serverTime : null,
      });
      setCheckFailed(false);
    };

    window.addEventListener(TIME_SYNC_ERROR_EVENT, handleTimeSyncError);
    return () => window.removeEventListener(TIME_SYNC_ERROR_EVENT, handleTimeSyncError);
  }, []);

  const displayedTimes = useMemo(() => {
    return {
      deviceTime: formatTime(snapshot?.deviceTime),
      serverTime: formatTime(snapshot?.serverTime),
    };
  }, [snapshot?.deviceTime, snapshot?.serverTime]);

  const handleRecheck = useCallback(async () => {
    setIsChecking(true);
    setCheckFailed(false);

    try {
      const response = await authAxiosInstance.get("/User/profile", {
        headers: {
          [TIME_SYNC_RECHECK_HEADER]: "1",
        },
      });

      if (isTimeSyncAuthError(response.data) || Number(response.data?.code) !== 0) {
        setSnapshot({
          deviceTime: Date.now(),
          serverTime: normalizeServerTime(response.data?.server_time) ?? snapshot?.serverTime ?? null,
        });
        setCheckFailed(true);
        return;
      }

      setSnapshot(null);
      setCheckFailed(false);
      void queryClient.refetchQueries({ type: "active" });
    } catch (error) {
      if (isTimeSyncError(error)) {
        setSnapshot({
          deviceTime: Date.now(),
          serverTime: (error as { snapshot?: TimeSyncSnapshot }).snapshot?.serverTime ?? snapshot?.serverTime ?? null,
        });
      }
      setCheckFailed(true);
    } finally {
      setIsChecking(false);
    }
  }, [queryClient, snapshot?.serverTime]);

  if (!snapshot) return null;

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-base-300 px-4 py-6 text-base-content">
      <div className="w-full max-w-[420px] rounded-lg bg-base-200 p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-warning/15 text-warning">
            <ClockAlert size={22} aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold leading-tight">
              {t("toast:timeSyncTitle", "Please check your device time")}
            </h1>
            <p className="mt-2 text-sm font-semibold leading-snug text-base-content/70">
              {t(
                "toast:timeSyncDescription",
                "Your device time does not match the server time. Update your date and time settings, then try again."
              )}
            </p>
          </div>
        </div>

        <div className="mt-4 grid gap-2">
          <div className="rounded-lg bg-base-300 p-2">
            <p className="text-[12px] font-semibold uppercase text-base-content/50">
              {t("toast:timeSyncDeviceTime", "Device time")}
            </p>
            <p className="mt-1 text-sm font-bold text-base-content">{displayedTimes.deviceTime}</p>
          </div>
          <div className="rounded-lg bg-base-300 p-2">
            <p className="text-[12px] font-semibold uppercase text-base-content/50">
              {t("toast:timeSyncServerTime", "Server time")}
            </p>
            <p className="mt-1 text-sm font-bold text-base-content">{displayedTimes.serverTime}</p>
          </div>
        </div>

        {checkFailed && (
          <p className="mt-3 text-sm font-semibold leading-snug text-error">
            {t("toast:timeSyncStillMismatch", "The time still does not match. Please update your device time and retry.")}
          </p>
        )}

        <button
          type="button"
          className="btn btn-primary btn-soft mt-4 h-11 w-full"
          onClick={handleRecheck}
          disabled={isChecking}
        >
          <RefreshCw size={18} className={cn(isChecking && "animate-spin")} aria-hidden="true" />
          {isChecking
            ? t("toast:timeSyncChecking", "Checking")
            : t("toast:timeSyncRetry", "Recheck")}
        </button>
      </div>
    </div>
  );
}
